/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, October 16th 2021, 1:38:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Alert, { AlertProps } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Snackbar, { SnackbarOrigin, SnackbarProps } from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton } from "@mui/material";

export interface OSAlertProps extends AlertProps {
  alertTitle?: React.ReactNode;
}

export const OSAlert = (props: OSAlertProps) => {
  return (
    <Alert className="os-alert" variant={props.variant ?? "standard"} severity={props.severity ?? "info"}>
      {props.alertTitle ? <AlertTitle>{props.alertTitle}</AlertTitle> : null}
      {props.children}
    </Alert>
  );
};

export interface CustomSnackBarProps extends SnackbarProps {
  open: boolean;
  children?: React.ReactElement;
}

const CustomSnackBar = ({
  open,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  onClose,
  autoHideDuration,
  children,
  ...props
}: CustomSnackBarProps) => {
  const handleCloseButIgnoreClickaway = (event: React.SyntheticEvent | React.MouseEvent, reason?: any) => {
    if (reason === "clickaway") {
      return;
    }

    onClose(event, reason);
  };

  return (
    <Snackbar
      {...props}
      anchorOrigin={anchorOrigin}
      open={open}
      onClose={handleCloseButIgnoreClickaway}
      autoHideDuration={autoHideDuration}
      key={anchorOrigin.vertical + anchorOrigin.horizontal}
    >
      {children}
    </Snackbar>
  );
};

export interface AlertSnackBarProps
  extends Partial<SnackbarOrigin & Pick<CustomSnackBarProps, "open" | "autoHideDuration"> & AlertProps> {
  message: React.ReactNode;
  handleClose: () => void;
  autoClose?: boolean;
  canBeClosedByUser?: boolean;
}

export const AlertSnackBar = ({
  open,
  severity = "error",
  variant = "filled",
  message,
  autoClose,
  handleClose,
  canBeClosedByUser = true,
  vertical = "top",
  horizontal = "center",
  autoHideDuration,
  ...props
}: AlertSnackBarProps) => {
  return (
    <CustomSnackBar
      open={open}
      anchorOrigin={{ vertical, horizontal }}
      onClose={handleClose}
      autoHideDuration={autoHideDuration ?? (autoClose ? 5000 : null)}
    >
      <Alert
        variant={variant}
        onClose={canBeClosedByUser ? () => handleClose() : undefined}
        severity={severity}
        sx={{ width: "100%" }}
        {...props}
      >
        {message}
      </Alert>
    </CustomSnackBar>
  );
};

interface ActionSnackbarProps extends SnackbarProps {
  open: boolean;
  handleClose: () => void;
  handleOpen?: () => void;
  message: React.ReactNode;
  actionFunction?: () => void;
  actionText: React.ReactNode;
  showCloseButton?: boolean;
}

export const ActionSnackbar = ({
  open,
  handleClose,
  handleOpen = null,
  message,
  actionFunction = null,
  actionText,
  showCloseButton = true,
  ...props
}: ActionSnackbarProps) => {
  const action = (
    <>
      {actionFunction ? (
        <Button color="primary" size="small" onClick={actionFunction}>
          {actionText}
        </Button>
      ) : null}
      {showCloseButton && (
        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );

  return (
    <>
      <CustomSnackBar
        {...props}
        open={open}
        autoHideDuration={props.autoHideDuration ?? 6000}
        onClose={handleClose}
        message={message}
        action={action}
      />
    </>
  );
};
